import { FormEvent, useState } from "react"
import { router } from "@inertiajs/react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"

export type MembershipRow = {
  id: number
  program: string
  source: "code" | "admin" | "signup" | "purchase"
  code: string | null
  granted_at: string
  expires_at: string | null
  expired: boolean
}

const SOURCE_LABELS: Record<MembershipRow["source"], string> = {
  code: "Access code",
  admin: "Added by an admin",
  signup: "Signed up",
  purchase: "Purchase",
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
}

/** Program access for one user (admin view): memberships, and instructor assignments. */
export function UserPrograms({
  userId,
  userEmail,
  isInstructor,
  programs,
  memberships,
  taughtProgramIds,
}: {
  userId: number
  userEmail: string
  isInstructor: boolean
  programs: { id: number; name: string }[]
  memberships: MembershipRow[]
  taughtProgramIds: number[]
}) {
  const [programId, setProgramId] = useState(String(programs[0]?.id ?? ""))
  const [expiresOn, setExpiresOn] = useState("")
  const [taught, setTaught] = useState<number[]>(taughtProgramIds)

  const grant = (e: FormEvent) => {
    e.preventDefault()
    router.post(
      `/admin/users/${userId}/memberships`,
      { program_id: programId, expires_on: expiresOn },
      { preserveScroll: true, onSuccess: () => setExpiresOn("") },
    )
  }

  const revoke = (membership: MembershipRow) =>
    router.delete(`/admin/users/${userId}/memberships/${membership.id}`, { preserveScroll: true })

  const saveTaught = (e: FormEvent) => {
    e.preventDefault()
    router.put(`/admin/users/${userId}/instructorships`, { program_ids: taught }, { preserveScroll: true })
  }

  const toggleTaught = (id: number, on: boolean) =>
    setTaught((ids) => (on ? [...ids, id] : ids.filter((x) => x !== id)))

  return (
    <>
      <section className="mt-10 max-w-md space-y-3" aria-label="Program access">
        <h2>Program access</h2>
        {memberships.length === 0 ? (
          <p>{userEmail} has no access to any program&rsquo;s official content.</p>
        ) : (
          <ul className="list-none space-y-3 pl-0">
            {memberships.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-2">
                <strong>{m.program}</strong>
                {m.expired ? (
                  <Badge tone="signal">Ended {formatDate(m.expires_at!)}</Badge>
                ) : m.expires_at ? (
                  <Badge tone="neutral">Until {formatDate(m.expires_at)}</Badge>
                ) : (
                  <Badge tone="accent">Full access</Badge>
                )}
                <span className="text-xs text-ink-muted">
                  {SOURCE_LABELS[m.source]}
                  {m.code ? ` (${m.code})` : ""}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  aria-label={`Remove access to ${m.program}`}
                  onClick={() => revoke(m)}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}

        {programs.length > 0 && (
          <form onSubmit={grant} className="space-y-3 pt-2">
            <div className="space-y-2">
              <label htmlFor="grant-program">Grant access to</label>
              <Select id="grant-program" value={programId} onChange={(e) => setProgramId(e.target.value)}>
                {programs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <label htmlFor="grant-expires">Access ends (optional)</label>
              <Input id="grant-expires" type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
              <p className="text-xs text-ink-muted">Leave empty for permanent access. Granting again replaces the end date.</p>
            </div>
            <Button type="submit">Grant access</Button>
          </form>
        )}
      </section>

      {isInstructor && (
        <form onSubmit={saveTaught} className="mt-10 max-w-md space-y-3" aria-label="Instructor programs">
          <h2>Programs they manage</h2>
          <p>An instructor can edit official content only in the programs checked here.</p>
          {programs.map((p) => (
            <label key={p.id} className="flex cursor-pointer items-center gap-2 font-normal text-ink-body">
              <Checkbox checked={taught.includes(p.id)} onChange={(e) => toggleTaught(p.id, e.target.checked)} />
              {p.name}
            </label>
          ))}
          <Button type="submit">Save programs</Button>
        </form>
      )}
    </>
  )
}
